import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import ProductTaggerImproved from '../components/ProductTaggerImproved';
import { api } from '../hooks/useAuth';

const SERVER_BASE = (process.env.REACT_APP_BASE_URL || api.defaults.baseURL || window.location.origin || '').replace(/\/$/, '');

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://')) return url.replace('http://', 'https://');
  if (url.startsWith('https://')) return url;
  return `${SERVER_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

export default function ReelManager() {
  const [reels, setReels] = useState([]);
  const [liveReels, setLiveReels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [selectedReel, setSelectedReel] = useState(null);
  const [tab, setTab] = useState('saved');
  const [message, setMessage] = useState(null);

  useEffect(() => { fetchSavedReels(); }, []);

  const fetchSavedReels = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/auth/facebook/reels/saved');
      setReels(data.reels || []);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load reels' });
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveReels = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get('/api/auth/facebook/reels');
      setLiveReels(data.reels || []);
      setTab('sync');
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Instagram not connected' });
    } finally {
      setSyncing(false);
    }
  };

  const saveReels = async () => {
    if (!liveReels.length) return;
    setSyncing(true);
    try {
      await api.post('/api/auth/facebook/reels/save', { reels: liveReels });
      setMessage({ type: 'success', text: `${liveReels.length} reels saved!` });
      await fetchSavedReels();
      setTab('saved');
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to save reels' });
    } finally {
      setSyncing(false);
    }
  };

  const deleteReel = async (reelId) => {
    setDeleting(reelId);
    try {
      await api.delete(`/api/reels/${reelId}`);
      setMessage({ type: 'success', text: '✅ Reel deleted successfully' });
      await fetchSavedReels();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to delete reel' });
    } finally {
      setDeleting(null);
    }
  };

  const downloadReel = async (reel) => {
    const reelId = reel.video_id || reel.id;
    setDownloading(reelId);
    try {
      const { data } = await api.post('/api/reels/download-from-instagram', {
        media_url: reel.media_url,
        video_id: reelId,
        caption: reel.caption,
        thumbnail_url: reel.thumbnail_url,
        permalink: reel.permalink
      });
      setMessage({ type: 'success', text: '✅ Reel downloaded and saved!' });
      await fetchSavedReels();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || '❌ Download failed' });
    } finally {
      setDownloading(null);
    }
  };

  const getFilteredReels = () => {
    if (tab === 'sync') return liveReels;
    if (tab === 'saved') return reels;
    if (tab === 'source_synced') return reels.filter(r => r.media_url?.includes('instagram') && !r.media_url?.startsWith('/uploads'));
    if (tab === 'source_uploaded') return reels.filter(r => r.upload_source === 'manual_upload');
    if (tab === 'source_downloaded') return reels.filter(r => r.upload_source === 'downloaded');
    if (tab === 'source_url') return reels.filter(r => r.upload_source === 'url_fetched' || (!isNaN(r.video_id) === false && r.upload_source !== 'manual_upload' && r.media_url?.startsWith('/uploads')));
    return reels;
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Reel Manager</h1>
            <p className="page-subtitle">Sync, manage, and tag your Instagram reels</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={fetchLiveReels} disabled={syncing}>
              {syncing ? <><span className="spinner" /> Fetching...</> : '⟳ Sync from Instagram'}
            </button>
            {liveReels.length > 0 && tab === 'sync' && (
              <button className="btn btn-primary" onClick={saveReels} disabled={syncing}>
                💾 Save {liveReels.length} Reels
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} onClick={() => setMessage(null)} style={{ cursor: 'pointer', marginBottom: 24 }}>
            {message.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {[
            ['saved', 'All Saved'],
            ['sync', '📥 Live Sync'],
            ['source_synced', '📱 Instagram Synced'],
            ['source_uploaded', '📤 Uploaded'],
            ['source_downloaded', '⬇️ Downloaded'],
            ['source_url', '🔗 URL Fetched']
          ].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} className={`tab-button ${tab === key ? 'active' : ''}`} style={{ fontSize: 12, padding: '10px 16px' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-screen"><span className="spinner" /> Loading reels...</div>
        ) : (
          <div className="reel-grid">
            {getFilteredReels().map((reel) => (
              <ReelCard
                key={reel.video_id || reel.id}
                reel={reel}
                isSaved={tab !== 'sync'}
                onTag={() => setSelectedReel(reel)}
                onDelete={deleteReel}
                onDownload={downloadReel}
                isDeleting={deleting === (reel.video_id || reel.id)}
                isDownloading={downloading === (reel.video_id || reel.id)}
              />
            ))}
            {getFilteredReels().length === 0 && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0', color: 'var(--text-dim)' }}>
                📭 No reels found in this category.
              </div>
            )}
          </div>
        )}

        {selectedReel && (
          <ProductTaggerImproved
            reel={selectedReel}
            onClose={() => { setSelectedReel(null); fetchSavedReels(); }}
          />
        )}
      </main>
    </div>
  );
}

function ReelCard({ reel, isSaved, onTag, onDelete, onDownload, isDeleting, isDownloading }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const tagCount = reel.tagged_products?.filter(p => p.id).length || 0;
  const reelId = reel.video_id || reel.id;
  
  const isInstagramHosted = reel.media_url?.includes('instagram') || reel.media_url?.includes('cdninstagram');
  const isManualUpload = reel.upload_source === 'manual_upload';
  const isDownloaded = reel.upload_source === 'downloaded';
  const isUrlFetched = reel.upload_source === 'url_fetched' || (!isNaN(reel.video_id) === false && reel.upload_source !== 'manual_upload' && reel.media_url?.startsWith('/uploads'));
  
  const isLocalVideo = isManualUpload || isDownloaded || isUrlFetched;
  const thumbnailUrl = getMediaUrl(reel.thumbnail_url);
  const mediaUrl = getMediaUrl(reel.media_url);

  return (
    <div className="reel-card">
      {tagCount > 0 && <div className="reel-tag-count">🏷 {tagCount}</div>}
      
      {isInstagramHosted && !isLocalVideo && (
        <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255, 77, 109, 0.9)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, zIndex: 5 }}>
          📱 Instagram
        </div>
      )}

      {isManualUpload && (
        <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(72, 202, 228, 0.9)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, zIndex: 5 }}>
          📤 Uploaded
        </div>
      )}

      {isDownloaded && (
        <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(100, 200, 100, 0.9)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, zIndex: 5 }}>
          ⬇️ Downloaded
        </div>
      )}

      {isUrlFetched && (
        <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(155, 93, 229, 0.9)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, zIndex: 5 }}>
          🔗 URL Fetched
        </div>
      )}

      {isLocalVideo && mediaUrl ? (
        <video src={mediaUrl} className="reel-thumbnail" muted playsInline preload="metadata" style={{ width: '100%', objectFit: 'cover', display: 'block' }} onMouseOver={e => e.currentTarget.play()} onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }} />
      ) : thumbnailUrl ? (
        <img src={thumbnailUrl} alt={reel.caption || 'Reel'} className="reel-thumbnail" />
      ) : (
        <div className="reel-thumbnail-placeholder">🎬</div>
      )}

      <div className="reel-info">
        <p className="reel-caption">{reel.caption || 'No caption'}</p>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {isSaved ? (
            <>
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={onTag}>🏷 Tag</button>
              {isInstagramHosted && !isLocalVideo && (
                <button className="btn btn-secondary btn-sm" onClick={() => onDownload(reel)} disabled={isDownloading}>
                  {isDownloading ? <span className="spinner" /> : '⬇️'}
                </button>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => setShowConfirm(true)} disabled={isDeleting}>
                {isDeleting ? <span className="spinner" /> : '🗑️'}
              </button>
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 0', textAlign: 'center', width: '100%' }}>Click "Save Reels" to manage</div>
          )}
          {reel.permalink && <a className="btn btn-ghost btn-sm" href={reel.permalink} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 10px' }}>📱</a>}
        </div>
      </div>

      {showConfirm && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.95)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, borderRadius: 14, zIndex: 10 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>Delete this reel?</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16, textAlign: 'center' }}>This will also remove all product tags</div>
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
            <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => onDelete(reelId)}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}
